import React, { useState } from 'react';
import './tripSearchPanel.css';

// The expanded Active-trips search: the existing search box stays where it is (TruckMatePage renders it), this is
// everything under it — labeled filters, a summary with Reset, and the matching trips as buttons. It only reads and
// sets the page's existing filter state and selection; closing it never clears anything.
const TZ = 'America/New_York';
const ymd = (ms) => new Date(ms).toLocaleDateString('en-CA', { timeZone: TZ });
const shortDay = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
const PAGE = 24;

// "Miami · Oct 21 · Route NE · DEPART SHIPPER · Outside carriers only" — or "No filters". Pure.
export function filterSummary({ runHub, runDay, runRoute, statusFilter, billType, billLabel = (x) => x, ocOnly, downOnly = false }) {
  const parts = [
    runHub !== 'all' ? runHub : null, runDay ? shortDay(runDay) : null, runRoute !== 'all' ? `Route ${runRoute}` : null,
    statusFilter !== 'all' ? statusFilter : null, billType !== 'all' ? billLabel(billType) : null, ocOnly ? 'Outside carriers only' : null, downOnly ? 'Broken down only' : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'No filters — every trip on the board';
}

export default function TripSearchPanel({
  id, trips, runs, runOn,
  runHub, setRunHub, runDay, setRunDay, runRoute, setRunRoute, routeOptions, recentDays, dayLabel,
  statusFilter, setStatusFilter, statusOptions, billType, setBillType, billTypeOptions, billLabel, ocOnly, setOcOnly, ocCount, downOnly = false, setDownOnly = () => {}, downCount = 0,
  selectedTrip, onSelect, onReset, onDone = null, active = true, now = Date.now(),
}) {
  const [shown, setShown] = useState(PAGE);
  const today = ymd(now); const yesterday = ymd(now - 86400000);
  const quick = [[today, 'Today'], [yesterday, 'Yesterday'], ...recentDays.filter((d) => d !== today && d !== yesterday).slice(0, 3).map((d) => [d, dayLabel(d)])];
  const routeOf = (t) => (runs[t.tripNumber] && runs[t.tripNumber].route) || null;
  // LTL search on → grouped by route (as before); otherwise the board's own order
  const list = trips.slice(0, shown);
  const groups = runOn ? Object.entries(list.reduce((g, t) => { const k = routeOf(t) || 'No route assigned'; (g[k] = g[k] || []).push(t); return g; }, {})) : [[null, list]];
  const summary = filterSummary({ runHub, runDay, runRoute, statusFilter, billType, billLabel, ocOnly, downOnly });
  // a plain render function (not a component made inside render — that would remount and lose focus)
  const result = (t) => {
    const sel = String(selectedTrip || '') === String(t.tripNumber);
    return (
      <button key={t.tripNumber} type="button" className="tsp-result" aria-pressed={sel} onClick={() => onSelect(String(t.tripNumber))}>
        <b>{t.powerUnit ? `Truck ${t.powerUnit}` : 'No truck assigned'}</b>
        <span>Trip {t.tripNumber}</span>
        <span>{routeOf(t) ? `Route ${routeOf(t)}` : 'No route assigned'}</span>
      </button>
    );
  };
  return (
    <div id={id} className="tsp" role="region" aria-label="Search and filters">
      <p className="tsp-help">Also searches trailers and broker load numbers.</p>

      <fieldset className="tsp-group">
        <legend>LTL runs <span>(Miami / Ventura runs on ARTIS or a trip sheet — other trips are hidden while these are set)</span></legend>
        <div className="tsp-grid">
          <label className="tsp-field"><span>Departure hub</span>
            <select value={runHub} onChange={(e) => { setRunHub(e.target.value); setRunRoute('all'); }}>
              <option value="all">All hubs</option><option value="Miami">Miami</option><option value="Ventura">Ventura</option>
            </select>
          </label>
          <div className="tsp-field">
            <label htmlFor={`${id}-date`}>Departure date</label>
            <input id={`${id}-date`} type="date" value={runDay} onChange={(e) => setRunDay(e.target.value)} />
            <div className="tsp-quick" role="group" aria-label="Quick dates">
              {quick.map(([d, label]) => <button key={d} type="button" aria-pressed={runDay === d} onClick={() => setRunDay(runDay === d ? '' : d)}>{runDay === d ? '✓ ' : ''}{label}</button>)}
            </div>
          </div>
          <label className="tsp-field"><span>Route</span>
            <select value={runRoute} onChange={(e) => setRunRoute(e.target.value)}>
              <option value="all">All routes</option>
              {routeOptions.map(([r, n]) => <option key={r} value={r}>{r}{n ? ` (${n})` : ''}</option>)}
            </select>
          </label>
        </div>
      </fieldset>

      <div className="tsp-grid tsp-row2">
        <label className="tsp-field"><span>Trip status</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            {statusOptions.map(([label, n]) => <option key={label} value={label}>{label} ({n})</option>)}
          </select>
        </label>
        <label className="tsp-field"><span>Bill type</span>
          <select value={billType} onChange={(e) => setBillType(e.target.value)}>
            <option value="all">All bill types</option>
            {billTypeOptions.map(([p, n]) => <option key={p} value={p}>{billLabel(p)} · {n}</option>)}
          </select>
        </label>
        <div className="tsp-checks">
          <label className="tsp-check"><input type="checkbox" checked={ocOnly} onChange={(e) => setOcOnly(e.target.checked)} /> Outside carriers only <small>({ocCount})</small></label>
          <label className="tsp-check"><input type="checkbox" checked={downOnly} onChange={(e) => setDownOnly(e.target.checked)} /> Broken down only <small>({downCount})</small></label>
        </div>
      </div>

      <div className="tsp-summary">
        <span>{summary}</span>
        <span className="tsp-actions">
          <button type="button" className="tsp-btn" onClick={() => { onReset(); setShown(PAGE); }}>Reset filters</button>
          {onDone && <button type="button" className="tsp-btn tsp-done" onClick={onDone}>Done</button>}
        </span>
      </div>

      {active && (
      <div className="tsp-results">
        <p className="tsp-count" aria-live="polite">{trips.length} matching trip{trips.length === 1 ? '' : 's'}</p>
        {trips.length === 0
          ? <p className="tsp-empty">No trips match — change the search or Reset filters.</p>
          : groups.map(([route, items]) => (
            <div key={route || 'all'} className="tsp-route">
              {route && <div className="tsp-route-name">{route}</div>}
              <div className="tsp-list">{items.map(result)}</div>
            </div>
          ))}
        {trips.length > shown && <button type="button" className="tsp-btn tsp-more" onClick={() => setShown((n) => n + PAGE)}>Show {Math.min(PAGE, trips.length - shown)} more of {trips.length - shown}</button>}
      </div>
      )}
    </div>
  );
}
